import { ApiError, noticeSessionEnded } from './api';

const BASE_URL = import.meta.env['VITE_API_BASE_URL'] || '/api';

/**
 * Write requests.
 *
 * Separate from the read client because the failure modes are different and the
 * caller has to distinguish them. A 409 is not an error to retry — somebody else
 * changed the record — and a 422 is not a bug, it is the shop's own rules
 * refusing the change.
 */
export class ConflictError extends ApiError {
  constructor(message: string) {
    super(409, message);
    this.name = 'ConflictError';
  }
}

export class RuleError extends ApiError {
  constructor(message: string) {
    super(422, message);
    this.name = 'RuleError';
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string) {
    super(403, message);
    this.name = 'ForbiddenError';
  }
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError(0, 'Cannot reach QuickStock. Your change has not been saved.');
  }

  noticeSessionEnded(response);
  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    const message = payload?.error ?? `Request failed (${response.status})`;

    if (response.status === 409) throw new ConflictError(message);
    if (response.status === 422) throw new RuleError(message);
    if (response.status === 403) throw new ForbiddenError(message);
    throw new ApiError(response.status, message);
  }

  return payload as T;
}

export const mutate = {
  post: <T>(path: string, body?: unknown): Promise<T> => send<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => send<T>('PATCH', path, body),
  delete: <T>(path: string): Promise<T> => send<T>('DELETE', path),
};

/** Roles that may write, matching the server's capability table. */
export type StoreRole = 'owner' | 'manager' | 'viewer';

export function canWrite(role: StoreRole | undefined): boolean {
  return role === 'owner' || role === 'manager';
}

export function canManageUsers(role: StoreRole | undefined): boolean {
  return role === 'owner';
}
